import {
  mdiPlus,
  mdiNoteEdit,
  mdiArrowTopRight,
  mdiHistory,
  mdiFilterVariant,
} from "@mdi/js";
import Icon from "@mdi/react";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../ui/new_components/common/drawer_modal";
import EntityHistory, {
  GetMasterLogsByEntityApi,
} from "../../purchase_order/purchase_order_create/components/entity_logs";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import {
  fetchGet,
  fetchPost,
  toQueryString,
} from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import {
  WAREHOUSE_TYPE,
  WAREHOUSE_SUB_LEVEL_OPTIONS,
  WarehouseModel,
} from "../models/warehouse_model";
import Pagination from "../../../ui/new_components/common/pagination";
import { debounce } from "lodash";
import moment from "moment";
import { ACTION, MODULE } from "../../../utils/enums";
import { useAuth } from "../../../context_providers/auth/authProvider";
import { parse } from "papaparse";
import {
  ResponsiveListShell,
  FilterSheet,
  DateRangeFilter,
  MobileRecordCard,
  MobileRecordCardList,
  EmptyState,
} from "../../../ui/new_components/common/responsive";

interface InventoryBasketListScreenProps {}

export interface WarehouseFilterData {
  name?: string;
  id?: string;
  type?: any;
  sub_level?: string;
  description?: string;
  city?: string;
  state?: string;
  pincode?: string;
  from?: Date;
  warehouse_id?: string;
  to?: Date;
  page: number;
  count: number;
  search?: string;
  all?: any;
}

const defaultFilter: WarehouseFilterData = {
  page: 1,
  count: 100,
};

const InventoryBasketListScreen: FunctionComponent<
  InventoryBasketListScreenProps
> = () => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/warehouse_type`;
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<WarehouseFilterData>(defaultFilter);
  const [selectedType, setSelectedType] = useState<string>("");
  const [updateDisabled, setUpdateDisabled] = useState(true);
  const [selected, setSelected] = useState<WarehouseModel[]>([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(50);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<WarehouseModel[]>([]);
  const [search, setSearch] = useState("");
  const [showHistory, setShowHistory] = useState<string>();
  const [showFilter, setShowFilter] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768,
  );
  const { isAuthorised } = useAuth();
  const navaigation = useHistory();

  useEffect(() => {
    const disable = selected.length === 0;

    setUpdateDisabled(disable);
  }, [selected]);

  const [maker, setMaker] = useState(false);

  useEffect(() => {
    const authorised = isAuthorised({
      action: [ACTION.UPDATE, ACTION.CREATE, ACTION.READ],
      module: MODULE.warehouse,
      operator: "and",
    });
    setMaker(authorised);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (prop: WarehouseFilterData) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + toQueryString(prop));
      if (res.success) {
        setData(res.data.data);

        if (res.data.metadata) {
          setTotal(res.data.metadata.total);
        }
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
      //   return { success: false, error: error.message };
    }
    setLoading(false);
  };

  const getWarehouseExport = async () => {
    try {
      const a = document.createElement("a");
      a.target = "_blank";

      a.href = `${
        process.env.REACT_APP_BACKEND_BASE_URL
      }/api/v3/erp/inventory/warehouse_type/export${toQueryString({
        query: JSON.stringify({
          ...filter,
        }),
      })}`;

      // Delay the trigger to allow UI updates (if necessary)
      setTimeout(() => {
        setLoading(false);
        showToast({ type: ToastType.success, text: "Download started!" });
        a.click(); // Trigger the download
      }, 1000);
    } catch (error) {
      setLoading(false); // Make sure loading is set to false in case of an error
      showToast({ type: ToastType.error, text: "Failed to start download." });
    }
  };

  const debouncedHandler = useCallback(
    debounce((prop: WarehouseFilterData) => {
      if (prop) fetchData(prop);
    }, 500),
    [],
  );

  useEffect(() => {
    filter.type = selectedType;
    debouncedHandler(filter);
  }, [page, filter, selectedType, debouncedHandler]);

  useEffect(() => {
    if (count) fetchData({ page, count, search });
  }, [count]);

  const handleOnEdit = () => {
    if (selected.length > 1) {
      showToast({
        type: ToastType.error,
        text: "Please select one item to edit",
      });
    } else if (selected.length === 0) {
      showToast({
        type: ToastType.error,
        text: "Please select an item to edit",
      });
    } else {
      navaigation.push(
        urls.procurement_masters_warehouse + "/update/" + selected[0]!.id,
      );
    }
  };

  const warehouseFileInputRef = useRef<HTMLInputElement>(null);
  const handleUploadWarehouseClick = () => {
    if (warehouseFileInputRef.current) {
      warehouseFileInputRef.current.click();
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "Warehouse Name",
      "Type",
      "Mobile No",
      "Address Line 1",
      "City",
      "State",
      "Country",
      "Pincode",
    ];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "warehouse_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validateWarehouseRows = (
    rows: Record<string, string>[],
  ): { ok: true } | { ok: false; error: string } => {
    const requiredHeaders = [
      "Warehouse Name",
      "Type",
      "Address Line 1",
      "City",
      "State",
      "Country",
      "Pincode",
    ];
    if (rows.length === 0) return { ok: false, error: "CSV has no rows." };

    const presentHeaders = Object.keys(rows[0]);
    const missingHeaders = requiredHeaders.filter(
      (h) => !presentHeaders.includes(h),
    );
    if (missingHeaders.length) {
      return {
        ok: false,
        error: `Missing required column(s): ${missingHeaders.join(", ")}`,
      };
    }

    const allowedTypes = Object.values(WAREHOUSE_TYPE);
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNum = i + 2;
      const missing = requiredHeaders.filter(
        (h) => !(row[h] ?? "").toString().trim(),
      );
      if (missing.length) {
        return {
          ok: false,
          error: `Row ${rowNum}: missing ${missing.join(", ")}`,
        };
      }

      const type = (row["Type"] ?? "").toString().trim();
      if (!allowedTypes.includes(type)) {
        return {
          ok: false,
          error: `Row ${rowNum}: Type must be one of ${allowedTypes.join(", ")}`,
        };
      }

      const pincode = (row["Pincode"] ?? "").toString().trim();
      if (pincode.length !== 6) {
        return {
          ok: false,
          error: `Row ${rowNum}: Pincode must be exactly 6 characters`,
        };
      }
    }
    return { ok: true };
  };

  const handleUploadCSV = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validMimeTypes = ["text/csv", "application/vnd.ms-excel"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!validMimeTypes.includes(file.type) && fileExtension !== "csv") {
      showToast({ type: ToastType.error, text: "Only CSV files are allowed." });
      if (warehouseFileInputRef.current)
        warehouseFileInputRef.current.value = "";
      return;
    }

    const validation = await new Promise<
      { ok: true } | { ok: false; error: string }
    >((resolve) => {
      parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data }) => resolve(validateWarehouseRows(data)),
        error: (err) =>
          resolve({ ok: false, error: `Failed to parse CSV: ${err.message}` }),
      });
    });

    if (!validation.ok) {
      showToast({ type: ToastType.error, text: validation.error });
      if (warehouseFileInputRef.current)
        warehouseFileInputRef.current.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const upload_url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/warehouse_type/import-warehouses`;

    try {
      const res = await fetchPost(upload_url, formData);

      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        fetchData({ page, count, search });
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || "Upload failed.",
        });
      }
    } catch (err) {
      showToast({
        type: ToastType.error,
        text: "File upload failed. " + err,
      });
    } finally {
      if (warehouseFileInputRef.current)
        warehouseFileInputRef.current.value = "";
    }
  };

  return (
    <>
      {showHistory && (
        <DrawerModal
          location={DrawerModalLoaction.right}
          show={showHistory !== undefined}
          onClose={() => setShowHistory(undefined)}
          title="Warehouse History"
        >
          <EntityHistory
            fetchLogs={({ page, count }) =>
              GetMasterLogsByEntityApi({
                entityType: "warehouse",
                entityId: showHistory,
                page,
                count,
              })
            }
          />
        </DrawerModal>
      )}

      <ResponsiveListShell
        title="Warehouse Master"
        count={total}
        onRefresh={() => fetchData({ page, count, search })}
        actions={
          <>
            {maker && (
              <>
                <button
                  id="create_btn"
                  onClick={() => {
                    navaigation.push(
                      urls.procurement_masters_warehouse + "/create",
                    );
                  }}
                  className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
                >
                  <Icon path={mdiPlus} className="h-4 w-4" />
                  Create
                </button>

                <button
                  id="update_btn"
                  disabled={updateDisabled}
                  onClick={handleOnEdit}
                  className={`flex gap-1 items-center rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs ${
                    updateDisabled
                      ? "cursor-not-allowed opacity-70"
                      : "cursor-pointer"
                  }`}
                >
                  <Icon path={mdiNoteEdit} className="h-4 w-4" />
                  Update
                </button>
              </>
            )}

            <button
              id="export_btn"
              onClick={getWarehouseExport}
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
            >
              <Icon path={mdiArrowTopRight} className="h-4 w-4" />
              Export
            </button>

            {maker && (
              <>
                <input
                  type="file"
                  id="warehouse_file_input"
                  ref={warehouseFileInputRef}
                  accept=".csv"
                  onChange={handleUploadCSV}
                  className="hidden"
                />

                <label
                  id="download_template_btn"
                  onClick={handleDownloadTemplate}
                  title={"Download Template for warehouse upload"}
                  className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
                >
                  Download Template
                </label>

                <button
                  id="upload_warehouses_btn"
                  onClick={handleUploadWarehouseClick}
                  className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
                >
                  Upload Warehouses
                </button>
              </>
            )}
          </>
        }
        filterToggle={
          <div className="flex gap-2 items-center">
            <h6 className="font-normal text-xs">Filters</h6>
            <div
              id="warehouse_list_filter_toggle_btn"
              onClick={() => setShowFilter((v) => !v)}
              className="flex items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white cursor-pointer"
            >
              <Icon path={mdiFilterVariant} className="w-4 h-4" />
            </div>
          </div>
        }
      >
        <FilterSheet
          show={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={() => setShowFilter(false)}
          onReset={() => {
            setFilter(defaultFilter);
            setSelectedType("");
          }}
          gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2 items-start"
        >
          <div id="warehouse_list_created_at_filter">
            <label>Created At</label>
            <DateRangeFilter
              compact
              showChips={false}
              fromDate={
                filter.from ? moment(filter.from).format("YYYY-MM-DD") : ""
              }
              toDate={filter.to ? moment(filter.to).format("YYYY-MM-DD") : ""}
              onFromChange={(v) =>
                setFilter((o) => ({
                  ...o,
                  from: v
                    ? new Date(new Date(v).setHours(0, 0, 0, 0))
                    : undefined,
                }))
              }
              onToChange={(v) =>
                setFilter((o) => ({
                  ...o,
                  to: v
                    ? new Date(new Date(v).setHours(0, 0, 0, 0))
                    : undefined,
                }))
              }
            />
          </div>
          <div>
            <label htmlFor="warehouse_id">Warehouse ID</label>
            <input
              type="text"
              id="warehouse_id"
              placeholder="warehouse id"
              className="w-full bg-white rounded focus:outline-none border"
              value={filter?.warehouse_id || ""}
              onChange={(e) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  warehouse_id: e.target.value,
                }));
              }}
            />
          </div>
          <div>
            <label htmlFor="warehouse">Warehouse Name</label>
            <input
              type="text"
              id="warehouse"
              placeholder="warehouse"
              className="w-full bg-white rounded focus:outline-none border"
              value={filter?.name || ""}
              onChange={(e) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  name: e.target.value,
                }));
              }}
            />
          </div>
          <div>
            <label htmlFor="item_type">Warehouse Type</label>
            <select
              className="w-full bg-white rounded focus:outline-none"
              name="item_type"
              id="item_type"
              value={selectedType ?? ""}
              onChange={(e) => {
                setFilter((prevData) => ({
                  ...prevData,
                  type: e.target.value,
                }));
                setSelectedType(e.target.value);
                setSelected([]);
              }}
            >
              <option value="">select</option>
              {Object.values(WAREHOUSE_TYPE).map((f: string) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="sub_level">Sub-Level</label>
            <select
              className="w-full bg-white rounded focus:outline-none"
              id="sub_level"
              value={filter?.sub_level ?? ""}
              onChange={(e) => {
                setFilter((prevData) => ({
                  ...prevData,
                  sub_level: e.target.value,
                }));
              }}
            >
              <option value="">select</option>
              {WAREHOUSE_SUB_LEVEL_OPTIONS.map((s: string) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              placeholder="city"
              className="w-full bg-white rounded focus:outline-none border"
              value={filter?.city || ""}
              onChange={(e) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  city: e.target.value,
                }));
              }}
            />
          </div>
          <div>
            <label htmlFor="state">State</label>
            <input
              type="text"
              id="state"
              placeholder="state"
              className="w-full bg-white rounded focus:outline-none border"
              value={filter?.state || ""}
              onChange={(e) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  state: e.target.value,
                }));
              }}
            />
          </div>
          <div>
            <label htmlFor="pincode">Pincode</label>
            <input
              type="text"
              id="pincode"
              placeholder="pincode"
              className="w-full bg-white rounded focus:outline-none border"
              value={filter?.pincode || ""}
              onChange={(e) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  pincode: e.target.value,
                }));
              }}
            />
          </div>
        </FilterSheet>

        {data.length > 0 ? (
          <>
            <div className="hidden md:block my-2 border">
              <div className="overflow-auto">
                <table
                  id="warehouse_list_table"
                  className="w-full text-xs divide-y divide-gray-200 relative"
                >
                  <thead className="text-xs py-2 bg-white z-10">
                    <tr className="border">
                      <th className="p-3">
                        <div className="Select p-1">
                          <input
                            type="checkbox"
                            id="select_all_checkbox"
                            checked={selected.length === data.length}
                            onChange={(e) => {
                              setSelected(e.target.checked ? [...data] : []);
                            }}
                          />
                        </div>
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-center">
                        History
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Warehouse Id
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Created At
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Warehouse Name
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Warehouse Type
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Sub-Level
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Address Line1
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Address Line2
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Address Line3
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        City
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        State
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Country
                      </th>
                      <th className="px-3 py-3 whitespace-nowrap text-left">
                        Pincode
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((d, i) => (
                      <tr
                        key={i}
                        id={`warehouse_row_${d.id}`}
                        className="border text-center"
                      >
                        <td className="p-3">
                          <input
                            type="checkbox"
                            id={`warehouse_row_checkbox_${d.id}`}
                            checked={selected.some((v) => v.id === d.id)}
                            onChange={(e) => {
                              setSelected(
                                e.target.checked
                                  ? [...selected, d]
                                  : selected.filter((w) => w.id !== d.id),
                              );
                            }}
                          />
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-center">
                          <div
                            id={`warehouse_history_btn_${d.id}`}
                            className="cursor-pointer flex justify-center"
                            title="History"
                            onClick={() => setShowHistory(d.id)}
                          >
                            <Icon path={mdiHistory} size="16"></Icon>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-left text-blue-500 whitespace-nowrap">
                          <a
                            id={`warehouse_view_link_${d.id}`}
                            href={`${urls.procurement_masters_warehouse}/view/${d.id}`}
                          >
                            {d.id}
                          </a>
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          {moment(d?.created_at).format("YYYY-MM-DD")}
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          {d?.name ?? "--"}
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          {d.type}
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          {d.sub_level || "---"}
                        </td>
                        <td className="px-3 py-3 text-left">
                          {d.address?.line1 || "---"}
                        </td>
                        <td className="px-3 py-3 text-left">
                          {d.address?.line2 || "---"}
                        </td>
                        <td className="px-3 py-3 text-left">
                          {d.address?.line3 || "---"}
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          {d.address?.city || "---"}
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          {d.address?.state || "---"}
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          {d.address?.country || "---"}
                        </td>
                        <td className="px-3 py-3 text-left whitespace-nowrap">
                          {d.address?.pincode || "---"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <MobileRecordCardList>
              {data.map((d, i) => (
                <MobileRecordCard
                  id={`warehouse_row_mobile_${d.id}`}
                  key={i}
                  onClick={() =>
                    navaigation.push(
                      `${urls.procurement_masters_warehouse}/view/${d.id}`,
                    )
                  }
                  title={d.id}
                  fields={[
                    { label: "Name", value: d?.name ?? "-" },
                    { label: "Type", value: d.type },
                    { label: "Sub-Level", value: d.sub_level || "-" },
                    { label: "City", value: d.address?.city || "-" },
                    { label: "State", value: d.address?.state || "-" },
                    { label: "Pincode", value: d.address?.pincode || "-" },
                    {
                      label: "Created At",
                      value: moment(d?.created_at).format("YYYY-MM-DD"),
                    },
                  ]}
                  actions={[
                    {
                      icon: mdiHistory,
                      title: "History",
                      onClick: () => setShowHistory(d.id),
                    },
                  ]}
                />
              ))}
            </MobileRecordCardList>

            <div
              id="warehouse_list_pagination"
              className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2"
            >
              <Pagination
                itemCount={data.length}
                page={filter.page}
                total={total}
                count={filter.count}
                onChange={(page) => setFilter((o) => ({ ...o, page }))}
              />
            </div>
          </>
        ) : !loading ? (
          <EmptyState
            title="No Warehouses found!"
            message="Try adjusting your search or filters."
            imageSrc="/images/nodata.png"
          />
        ) : null}
      </ResponsiveListShell>

      {loading && <LoadingWidget></LoadingWidget>}
    </>
  );
};

export default InventoryBasketListScreen;
